import { useEffect, useState } from "react";
import { BarChart3, Loader2, Sparkles, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { fetchKpis, extraireKpis } from "@/lib/kpis";
import KpiTable from "@/components/kpis/KpiTable";
import KpiChart from "@/components/kpis/KpiChart";
import KpiRatios from "@/components/kpis/KpiRatios";
import CiseauxMiniChart from "@/components/kpis/CiseauxMiniChart";
import { fetchMemoParams } from "@/lib/memo";

export default function KpiSection({ dossierId, onCountChange }) {
    const [kpis, setKpis] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState("");
    const [error, setError] = useState("");
    const [commandes, setCommandes] = useState(null);
    useEffect(() => { fetchMemoParams(dossierId).then((p) => setCommandes(p?.parametres_rapport?.commandes_garanties_fin_annee ?? null)).catch(() => {}); }, [dossierId]);

    useEffect(() => {
        fetchKpis(dossierId)
            .then(setKpis)
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [dossierId]);

    useEffect(() => {
        if (!loading) onCountChange?.(kpis.length);
    }, [kpis.length, loading, onCountChange]);

    async function extract() {
        setBusy(true);
        setError("");
        try {
            const res = await extraireKpis(dossierId, setStatus);
            setKpis(await fetchKpis(dossierId));
            if (res.kpis.length === 0) {
                toast.warning("Aucun KPI trouvé", { description: res.message });
            } else {
                toast.success(`${res.kpis.length} exercice${res.kpis.length > 1 ? "s" : ""} extrait${res.kpis.length > 1 ? "s" : ""}`, {
                    description: `Documents analysés : ${res.documents_analyses.join(", ")}`,
                });
            }
        } catch (e) {
            setError(e.message);
            toast.error("Extraction impossible", { description: e.message });
        } finally {
            setBusy(false);
        }
    }

    const last = kpis[kpis.length - 1]?.donnees_extraites;

    return (
        <Card data-testid="kpi-section" className="border-slate-200 bg-white shadow-sm">
            <CardContent className="pt-6 space-y-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-accent flex items-center justify-center">
                            <BarChart3 className="h-4 w-4 text-primary" strokeWidth={1.75} />
                        </div>
                        <div>
                            <h2 className="font-display text-xl text-foreground">KPI financiers</h2>
                            <p className="text-xs text-muted-foreground">
                                {last?.extrait_le
                                    ? `Extraits automatiquement le ${new Date(last.extrait_le).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })} · ${last.modele}`
                                    : "Extraction automatique par IA depuis les PDF (liasses) et FEC (CSV) de la dataroom — modèle hybride, aucune saisie manuelle"}
                            </p>
                        </div>
                    </div>
                    <Button
                        data-testid="extract-kpis-button"
                        onClick={extract}
                        disabled={busy || loading}
                        className="rounded-full h-10 px-5 bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" strokeWidth={2} />}
                        {busy ? (status === "analyse" ? "Lecture des documents et analyse IA… (peut prendre 2-3 min)" : "Analyse…") : kpis.length ? "Ré-extraire les KPIs" : "Extraire les KPIs financiers"}
                    </Button>
                </div>

                {error && (
                    <p data-testid="kpi-error" className="flex items-start gap-2 text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
                    </p>
                )}

                {loading ? (
                    <p className="text-sm text-muted-foreground">Chargement…</p>
                ) : kpis.length === 0 ? (
                    <p data-testid="kpi-empty" className="text-sm text-muted-foreground">
                        Aucun KPI extrait. Déposez les bilans / liasses fiscales en PDF dans la dataroom puis lancez l&apos;extraction.
                    </p>
                ) : (
                    <>
                        <KpiTable kpis={kpis} />
                        <KpiRatios kpis={kpis} />
                        <CiseauxMiniChart dossierId={dossierId} />
                        <KpiChart kpis={kpis} commandesGaranties={commandes} />
                    </>
                )}
            </CardContent>
        </Card>
    );
}
